/**
 * Platform Organizations Query Hook
 *
 * Fetches all organizations for platform admin dashboard.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPlatformOrganizations } from '../../api/platform';
import { POLL_INTERVALS } from '../../lib/queryClient';
import { getErrorMessage } from '../../api/client';

interface UsePlatformOrganizationsOptions {
  /** Enable/disable automatic polling (default: true) */
  polling?: boolean;
  /** Custom polling interval in ms (default: 60000) */
  pollingInterval?: number;
}

export function usePlatformOrganizations(options: UsePlatformOrganizationsOptions = {}) {
  const { polling = true, pollingInterval = POLL_INTERVALS.EVENT_LIST } = options;

  const query = useQuery({
    queryKey: ['platform', 'organizations'],
    queryFn: getPlatformOrganizations,
    // Enable polling when specified
    refetchInterval: polling ? pollingInterval : false,
    // Stop polling when window is not visible
    refetchIntervalInBackground: false,
    // Consider data stale after half the poll interval
    staleTime: pollingInterval / 2,
  });

  return {
    // Data
    data: query.data ?? null,
    organizations: query.data?.organizations ?? [],
    total: query.data?.total ?? 0,

    // States
    loading: query.isLoading,
    error: query.error ? getErrorMessage(query.error) : null,
    isRefetching: query.isRefetching,
    isStale: query.isStale,

    // Timestamps
    dataUpdatedAt: query.dataUpdatedAt,

    // Actions
    refetch: query.refetch,
  };
}

/**
 * Hook for invalidating platform organizations data after mutations
 */
export function useInvalidatePlatformOrganizations() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['platform', 'organizations'] });
  };
}
